import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const PORTRAIT = "https://thiebaud-patrick.e-monsite.com/medias/images/74312061-2636046213101054-8009735577471549440-o.jpg";
const ATELIER_1 = "https://images.unsplash.com/photo-1578301978693-85fa9c026109?q=80&w=1200";
const ATELIER_2 = "https://images.unsplash.com/photo-1580136608260-4ebce02287b4?q=80&w=1200";

export default function About() {
    return (
        <main data-testid="about-page" className="pt-32 pb-24 min-h-screen">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                {/* Hero text */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="max-w-4xl mb-20"
                >
                    <div className="overline mb-4">L'artiste</div>
                    <h1 className="serif text-5xl md:text-7xl leading-[0.95] mb-8">
                        Patrick Mathurin,<br />
                        <span className="italic-serif text-gilt">peindre pour respirer</span>.
                    </h1>
                </motion.div>

                {/* Bento grid */}
                <div className="grid grid-cols-12 gap-4 md:gap-6 mb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="col-span-12 md:col-span-5 row-span-2"
                    >
                        <div className="aspect-[3/4] overflow-hidden bg-ink-800">
                            <img src={PORTRAIT} alt="Patrick Mathurin" className="w-full h-full object-cover" />
                        </div>
                        <p className="text-xs text-bone-400 mt-3 italic-serif">Patrick Mathurin, dans son atelier.</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                        className="col-span-12 md:col-span-7 bg-ink-850 border border-white/5 p-8 md:p-12"
                    >
                        <div className="overline mb-6">Histoire</div>
                        <h3 className="serif text-3xl md:text-4xl mb-6 leading-tight">
                            Une vocation découverte<br />sur le tard.
                        </h3>
                        <p className="text-bone-200 leading-relaxed mb-4 font-light">
                            En 2013, sans formation, sans projet précis, Patrick saisit un pinceau.
                            Ce qui commence comme un essai devient une discipline quotidienne, puis
                            une nécessité.
                        </p>
                        <p className="text-bone-200 leading-relaxed font-light">
                            Aujourd'hui, plus d'une centaine de toiles plus tard, il défend une peinture
                            classique, exigeante dans la lecture du visage, sensible à la lumière,
                            traversée par une fascination pour l'Orient.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="col-span-6 md:col-span-3 bg-ink-850 border border-white/5 p-6 md:p-8 flex flex-col justify-between"
                    >
                        <div>
                            <div className="serif text-5xl md:text-6xl text-gilt">70%</div>
                            <div className="overline mt-2">Portraitiste</div>
                        </div>
                        <p className="text-xs text-bone-400 mt-6 italic-serif">Toiles de lin ou de coton</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.25 }}
                        className="col-span-6 md:col-span-4 bg-ink-850 border border-white/5 p-6 md:p-8 flex flex-col justify-between"
                    >
                        <div>
                            <div className="serif text-5xl md:text-6xl text-gilt">30%</div>
                            <div className="overline mt-2">Divers</div>
                        </div>
                        <p className="text-xs text-bone-400 mt-6 italic-serif">Paysages, animaux, asiatique</p>
                    </motion.div>
                </div>

                {/* Quote */}
                <motion.section
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1 }}
                    className="py-20 md:py-32 border-t border-b border-white/5 mb-24"
                >
                    <blockquote className="italic-serif text-3xl md:text-5xl leading-tight text-center max-w-4xl mx-auto">
                        « Pour moi, une peinture n'est pas spécialement destinée à la vente.
                        C'est aussi une <span className="text-gilt">forme de méditation</span> : il n'y a rien de plus apaisant
                        que de peindre, dans le calme, parfois accompagné d'une musique douce. »
                    </blockquote>
                </motion.section>

                {/* Process */}
                <div className="grid md:grid-cols-12 gap-12 mb-24">
                    <div className="md:col-span-4">
                        <div className="overline mb-4">L'atelier</div>
                        <h2 className="serif text-4xl md:text-5xl leading-tight">
                            Le geste,<br />puis la <span className="italic-serif text-gilt">patience</span>.
                        </h2>
                    </div>
                    <div className="md:col-span-7 md:col-start-6 space-y-10">
                        {[
                            { n: "01", t: "Le calme", d: "Chaque toile commence dans le silence, ou avec une musique douce. La sérénité est la première couche." },
                            { n: "02", t: "L'huile, le lin", d: "Pour les portraits : peinture à l'huile liquide, sur toile de lin ou de coton. Le visage se construit par couches." },
                            { n: "03", t: "L'acrylique, l'espace", d: "Pour les grands formats : peinture acrylique en tube. Une matière plus vive, une autre temporalité." },
                            { n: "04", t: "La méditation", d: "Peindre, c'est s'évader, détourner l'esprit. Une quiétude qui ressemble à la méditation." },
                        ].map((s, i) => (
                            <motion.div
                                key={s.n}
                                initial={{ opacity: 0, x: 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.7, delay: i * 0.1 }}
                                className="flex gap-6 md:gap-10"
                            >
                                <div className="serif text-gilt text-2xl pt-1 min-w-[3rem]">{s.n}</div>
                                <div>
                                    <h4 className="serif text-2xl mb-2">{s.t}</h4>
                                    <p className="text-bone-300 leading-relaxed font-light">{s.d}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                <div className="text-center">
                    <Link to="/contact" className="btn-gilt" data-testid="about-contact-cta">
                        Écrire à l'artiste <ArrowRight size={14} />
                    </Link>
                </div>
            </div>
        </main>
    );
}
